import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import RichTextEditor from '../components/RichTextEditor';
import toast from 'react-hot-toast';
import {
  BellIcon,
  PhotoIcon,
  QuestionMarkCircleIcon,
  CursorArrowRaysIcon,
  UserGroupIcon,
  PaperAirplaneIcon,
  EyeIcon,
  BookmarkIcon,
} from '@heroicons/react/24/outline';

import QuizBuilder from '../components/composer/QuizBuilder';
import ActionDesigner from '../components/composer/ActionDesigner';
import MediaUploader from '../components/composer/MediaUploader';
import UserTargeting from '../components/composer/UserTargeting';
import NotificationPreview from '../components/composer/NotificationPreview';
import QuickActions from '../components/composer/QuickActions';
import { api } from '../services/api';

interface NotificationForm {
  title: string;
  subtitle?: string;
  body: string;
  type: string;
  priority: string;
  category?: string;
  icon_name?: string;
  color_hex?: string;
  image_url?: string;
  thumbnail_url?: string;
  video_url?: string;
  actions?: any[];
  quiz_data?: any;
  input_config?: any;
  expires_at?: string;
  targeting: {
    target_type: string;
    user_ids?: number[];
    segment?: string;
    exclude_ids?: number[];
    test_mode: boolean;
  };
  send_immediately: boolean;
  scheduled_time?: string;
  save_as_template: boolean;
  template_name?: string;
}

const notificationTypes = [
  { id: 'quiz', name: 'Quiz', icon: '🧩', tab: 'Quizzes Tab' },
  { id: 'news', name: 'News', icon: '📰', tab: 'News Tab' },
  { id: 'announcement', name: 'Announcement', icon: '📢', tab: 'Updates Tab' },
  { id: 'achievement', name: 'Achievement', icon: '🏆', tab: 'Wins Tab' },
  { id: 'promotion', name: 'Promotion', icon: '🎁', tab: 'Offers Tab' },
];

const priorities = [
  { id: 'low', name: 'Low', color: 'gray' },
  { id: 'normal', name: 'Normal', color: 'blue' },
  { id: 'high', name: 'High', color: 'orange' },
  { id: 'critical', name: 'Critical', color: 'red' },
];

// Categories removed - using unified type field instead

export default function NotificationComposer() {
  const [activeTab, setActiveTab] = useState('content');
  const [showPreview, setShowPreview] = useState(window.innerWidth >= 1024);
  const [isLoading, setIsLoading] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const mediaUploaderRef = React.useRef<any>(null);

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<NotificationForm>({
    defaultValues: {
      type: 'quiz',
      priority: 'normal',
      targeting: {
        target_type: 'all',
        test_mode: false,
      },
      send_immediately: true,
      save_as_template: false,
    },
  });

  const watchedValues = watch();
  const notificationType = watch('type');

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
      if (window.innerWidth < 1024) {
        setShowPreview(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-populate title for quiz type
  useEffect(() => {
    if (notificationType === 'quiz') {
      setValue('title', 'Quick Quiz');
      setValue('subtitle', '');
    }
  }, [notificationType, setValue]);

  // Load draft on component mount
  useEffect(() => {
    const savedDraft = localStorage.getItem('notification_draft');
    if (savedDraft) {
      try {
        const draft = JSON.parse(savedDraft);
        Object.keys(draft).forEach(key => {
          setValue(key as any, draft[key]);
        });
        toast.success('Draft loaded from last session');
      } catch (e) {
        console.error('Failed to load draft:', e);
      }
    }
  }, [setValue]);

  const handleSaveDraft = () => {
    localStorage.setItem('notification_draft', JSON.stringify(watchedValues));
    toast.success('Draft saved successfully!');
  };

  const handleClearDraft = () => {
    localStorage.removeItem('notification_draft');
    toast.success('Draft cleared');
  };

  const handlePreview = async () => {
    setIsLoading(true);
    try {
      const response = await api.post('/admin/notifications/preview', watchedValues);
      toast.success(`Preview ready: ${response.data.target_users_count} users targeted`);
      setPreviewData(response.data);
    } catch (error: any) {
      console.error('Preview error:', error.response?.data);
      const errorMessage = typeof error.response?.data?.detail === 'string' 
        ? error.response.data.detail 
        : error.response?.data?.detail?.[0]?.msg || 'Failed to preview';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (data: NotificationForm) => {
    // Show confirmation dialog
    setShowConfirmDialog(true);
  };

  const handleConfirmSend = async () => {
    setShowConfirmDialog(false);
    setIsLoading(true);
    
    try {
      // Prepare the data with all fields
      const sendData = {
        ...watchedValues,
        send_immediately: watchedValues.send_immediately ?? true,
      };

      const response = await api.post('/admin/notifications/send', sendData);
      toast.success(
        `Notification sent! ${response.data.notifications_created} notifications created for ${response.data.users_targeted} users`
      );
      
      // Clear draft after successful send
      handleClearDraft();
      
      // Reset form to default values
      reset({
        title: '',
        subtitle: '',
        body: '',
        type: 'quiz',
        priority: 'normal',
        icon_name: '',
        color_hex: '',
        image_url: '',
        thumbnail_url: '',
        video_url: '',
        actions: undefined,
        quiz_data: undefined,
        input_config: undefined,
        expires_at: '',
        targeting: {
          target_type: 'all',
          user_ids: [],
          segment: '',
          exclude_ids: [],
          test_mode: false,
        },
        send_immediately: true,
        scheduled_time: '',
        save_as_template: false,
        template_name: '',
      });
      
      // Clear media uploader state
      if (mediaUploaderRef.current?.clearMedia) {
        mediaUploaderRef.current.clearMedia();
      }
      
      // Clear preview data
      setPreviewData(null);
      
      // Reset to content tab
      setActiveTab('content');
      
    } catch (error: any) {
      console.error('Send error:', error.response?.data);
      const errorMessage = typeof error.response?.data?.detail === 'string' 
        ? error.response.data.detail 
        : error.response?.data?.detail?.[0]?.msg || 'Failed to send notification';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const tabs = [
    { id: 'content', name: 'Content', icon: BellIcon },
    { id: 'media', name: 'Media', icon: PhotoIcon },
    { id: 'interactive', name: 'Interactive', icon: CursorArrowRaysIcon },
    { id: 'targeting', name: 'Targeting', icon: UserGroupIcon },
  ];

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-7xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Notification Composer
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Create and send rich, interactive notifications to your users
        </p>
      </div>

      {/* Quick Actions Section */}
      <div className="mb-6">
        <QuickActions
          onSelectTemplate={(template) => {
            Object.keys(template).forEach(key => {
              setValue(key as any, template[key]);
            });
            toast.success('Template loaded!');
          }}
          onQuickSend={async (template) => {
            Object.keys(template).forEach(key => {
              setValue(key as any, template[key]);
            });
            // Wait for form to update then submit
            setTimeout(() => {
              handleSubmit(onSubmit)();
            }, 100);
          }}
        />
      </div>

      <div className={`grid grid-cols-1 ${showPreview ? 'xl:grid-cols-2' : ''} gap-6`}>
        {/* Composer Panel */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700">
          {/* Tabs */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex space-x-4 px-6 py-3">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-3 py-2 rounded-lg font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  <tab.icon className="w-5 h-5 mr-2" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <form onSubmit={handleSubmit(onSubmit)} className="p-6">
            {activeTab === 'content' && (
              <div className="space-y-6">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Title <span className="text-red-500">*</span>
                    {notificationType === 'quiz' && (
                      <span className="text-xs text-gray-500 ml-2">
                        (Auto-filled for quiz type)
                      </span>
                    )}
                  </label>
                  <input
                    {...register('title', { required: 'Title is required' })}
                    className={`w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 ${
                      notificationType === 'quiz' ? 'bg-gray-50 dark:bg-gray-800' : ''
                    }`}
                    placeholder={notificationType === 'quiz' ? 'Quick Quiz' : 'Notification title...'}
                    readOnly={notificationType === 'quiz'}
                  />
                  {errors.title && (
                    <p className="mt-1 text-sm text-red-500">{errors.title.message}</p>
                  )}
                </div>

                {/* Subtitle */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Subtitle
                  </label>
                  <input
                    {...register('subtitle')}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    placeholder="Optional subtitle..."
                  />
                </div>

                {/* Body - Hidden for quiz type */}
                {notificationType !== 'quiz' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Body <span className="text-red-500">*</span>
                    </label>
                    <Controller
                      name="body"
                      control={control}
                      rules={{ required: 'Body is required' }}
                      render={({ field }) => (
                        <RichTextEditor
                          value={field.value || ''}
                          onChange={field.onChange}
                          placeholder="Enter your notification message..."
                          minHeight="200px"
                        />
                      )}
                    />
                    {errors.body && (
                      <p className="mt-1 text-sm text-red-500">{errors.body.message}</p>
                    )}
                  </div>
                )}

                {/* Quiz Information Notice */}
                {notificationType === 'quiz' && (
                  <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <div className="flex items-start">
                      <QuestionMarkCircleIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-blue-700 dark:text-blue-300 font-medium">
                          Quiz Question Setup
                        </p>
                        <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                          For quiz notifications, add your question and answer options in the <strong>Interactive tab</strong>. 
                          The question text will automatically become the notification content.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Type & Priority */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Type
                    </label>
                    <select
                      {...register('type')}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    >
                      {notificationTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.icon} {type.name} → {type.tab}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Priority
                    </label>
                    <select
                      {...register('priority')}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    >
                      {priorities.map((priority) => (
                        <option key={priority.id} value={priority.id}>
                          {priority.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Category field removed - using unified type field instead */}

                {/* Color */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Custom Color
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      {...register('color_hex')}
                      className="h-10 w-20 border border-gray-300 dark:border-gray-600 rounded-lg"
                    />
                    <input
                      {...register('color_hex')}
                      className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                      placeholder="#4CAF50"
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'media' && (
              <MediaUploader
                ref={mediaUploaderRef}
                onImageUpload={(url) => setValue('image_url', url)}
                onThumbnailUpload={(url) => setValue('thumbnail_url', url)}
                onVideoUpload={(url) => setValue('video_url', url)}
                imageUrl={watchedValues.image_url}
                thumbnailUrl={watchedValues.thumbnail_url}
                videoUrl={watchedValues.video_url}
              />
            )}

            {activeTab === 'interactive' && (
              <div className="space-y-6">
                <div className="p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                  <p className="text-sm text-blue-600 dark:text-blue-400">
                    Add interactive elements to engage users directly within the notification
                  </p>
                </div>

                {/* Quiz Section */}
                <div>
                  <h3 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-3">
                    Quiz Component
                  </h3>
                  {watchedValues.type === 'quiz' ? (
                    <QuizBuilder
                      onChange={(quizData) => setValue('quiz_data', quizData)}
                      onQuestionChange={(question) => setValue('body', question)}
                    />
                  ) : (
                    <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg text-center">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Set notification type to "Quiz" in the Content tab to enable quiz builder
                      </p>
                    </div>
                  )}
                </div>

                {/* Action Buttons Section */}
                <div>
                  <h3 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-3">
                    Action Buttons
                  </h3>
                  <ActionDesigner
                    onChange={(actions) => setValue('actions', actions)}
                  />
                </div>
              </div>
            )}

            {activeTab === 'targeting' && (
              <UserTargeting
                onChange={(targeting) => setValue('targeting', targeting)}
              />
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row justify-between gap-4 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handlePreview}
                  className="px-3 py-2 text-sm sm:text-base sm:px-4 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors flex items-center border border-green-300 dark:border-green-700"
                >
                  <EyeIcon className="w-5 h-5 mr-2" />
                  Preview (Safe)
                </button>
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  className="px-3 py-2 text-sm sm:text-base sm:px-4 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center"
                >
                  <BookmarkIcon className="w-5 h-5 mr-2" />
                  Save Draft
                </button>
                {/* Mobile Preview Toggle */}
                <button
                  type="button"
                  onClick={() => setShowPreview(!showPreview)}
                  className="lg:hidden px-3 py-2 text-sm bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded-lg hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors flex items-center border border-purple-300 dark:border-purple-700"
                >
                  <EyeIcon className="w-5 h-5 mr-2" />
                  {showPreview ? 'Hide' : 'Show'} Preview
                </button>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full sm:w-auto px-4 py-2 text-sm sm:text-base sm:px-6 bg-gradient-to-r from-red-500 to-orange-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                <PaperAirplaneIcon className="w-5 h-5 mr-2" />
                {isLoading ? 'Sending...' : 'Send Notification'}
              </button>
            </div>
          </form>
        </div>

        {/* Preview Panel - Hidden on mobile by default */}
        {showPreview && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                Live Preview
              </h2>
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <NotificationPreview 
              title={watchedValues.title || ''}
              subtitle={watchedValues.subtitle}
              body={watchedValues.body || ''}
              imageUrl={watchedValues.image_url}
              type={watchedValues.type || 'standard'}
              category={watchedValues.category}
              quizData={watchedValues.quiz_data}
              actions={watchedValues.actions}
            />
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Confirm Send Notification
            </h3>
            <div className="space-y-3 mb-6">
              <p className="text-gray-600 dark:text-gray-400">
                You are about to send this notification to:
              </p>
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {watchedValues.targeting?.test_mode ? '1 user (Test Mode)' : 
                   `${watchedValues.targeting?.target_type === 'all' ? 'All users' : 
                    watchedValues.targeting?.target_type === 'specific' ? 
                    `${watchedValues.targeting?.user_ids?.length || 0} selected users` :
                    'Targeted segment'}`}
                </p>
              </div>
              <p className="text-sm text-yellow-600 dark:text-yellow-400">
                ⚠️ This action cannot be undone. The notification will be sent immediately.
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowConfirmDialog(false)}
                className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSend}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold"
              >
                Yes, Send Now
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}